"use client";

import * as React from "react";
import { ActionButtons } from "~/components/tool-ui/shared/action-buttons";
import { type Action } from "~/components/tool-ui/shared/schema";
import { cn, Separator } from "./_adapter";
import type {
  ApprovalCardProps,
  ApprovalDecision,
  ApprovalSection,
  ApprovalSectionItem,
} from "./schema";

import { icons, Check, X } from "lucide-react";

type LucideIcon = React.ComponentType<{ className?: string }>;

// Field-heavy cards (e.g. an ingest preview with many profiled columns) would
// otherwise render as one long scroll. When `collapsibleSections` is set the
// rows are capped to this many and the remainder revealed by a toggle.
export const COLLAPSED_SECTION_ITEM_LIMIT = 8;

// Truncate `sections` to at most `limit` total items, preserving section order
// and grouping: each section keeps however many of its items fit in the
// remaining budget, and once the budget is spent later sections are dropped.
// Returns the capped sections and how many items were hidden.
export function capApprovalSections(
  sections: readonly ApprovalSection[],
  limit: number
): { sections: ApprovalSection[]; hiddenCount: number } {
  let budget = limit;
  const capped: ApprovalSection[] = [];
  for (const section of sections) {
    if (budget <= 0) break;
    const items = section.items.slice(0, budget);
    budget -= items.length;
    capped.push({ ...section, items });
  }
  const total = sections.reduce((count, section) => count + section.items.length, 0);
  return { sections: capped, hiddenCount: Math.max(0, total - limit) };
}

function getLucideIcon(name: string): LucideIcon | null {
  const pascalName = name
    .split("-")
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join("");

  const Icon = icons[pascalName as keyof typeof icons];
  return Icon ?? null;
}

interface ApprovalCardReceiptProps {
  id: string;
  title: string;
  choice: ApprovalDecision;
  actionLabel?: string;
  className?: string;
}

function ApprovalCardReceipt({
  id,
  title,
  choice,
  actionLabel,
  className,
}: ApprovalCardReceiptProps) {
  const isApproved = choice === "approved";
  const displayLabel = actionLabel ?? (isApproved ? "Approved" : "Denied");

  return (
    <div
      className={cn(
        "flex w-full min-w-64 max-w-md flex-col",
        "text-foreground",
        "motion-safe:animate-in motion-safe:fade-in motion-safe:blur-in-sm motion-safe:zoom-in-95 motion-safe:duration-300 motion-safe:ease-[cubic-bezier(0.16,1,0.3,1)] motion-safe:fill-mode-both",
        className
      )}
      data-slot="approval-card"
      data-tool-ui-id={id}
      data-receipt="true"
      role="status"
      aria-label={displayLabel}
    >
      <div
        className={cn(
          "bg-card/60 flex w-full items-center gap-3 rounded-2xl border px-4 py-3 shadow-xs"
        )}
      >
        <span
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-full bg-muted",
            isApproved ? "text-primary" : "text-muted-foreground"
          )}
        >
          {isApproved ? <Check className="size-4" /> : <X className="size-4" />}
        </span>
        <div className="flex flex-col">
          <span className="text-sm font-medium">{displayLabel}</span>
          <span className="text-muted-foreground text-sm">{title}</span>
        </div>
      </div>
    </div>
  );
}

interface ApprovalSectionTableProps {
  items: readonly ApprovalSectionItem[];
  headers: NonNullable<ApprovalCardProps["sectionTableHeaders"]>;
}

// Table layout for a section's items: one row per item, three columns aligned to
// the item fields (label / value / description). Used when the card opts into
// `sectionTableHeaders` — a many-item section (e.g. an ingest column preview)
// scans far better as a table than as the default stacked list.
function ApprovalSectionTable({ items, headers }: ApprovalSectionTableProps) {
  return (
    <table className="w-full text-sm">
      <thead>
        <tr className="text-muted-foreground text-left text-xs">
          <th className="py-1 pr-3 font-medium whitespace-nowrap">{headers.label}</th>
          <th className="py-1 pr-3 font-medium whitespace-nowrap">{headers.value}</th>
          <th className="w-full py-1 font-medium">{headers.description}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((item, index) => (
          <tr key={index} className="border-t align-top">
            <td className="py-1.5 pr-3 text-xs whitespace-nowrap">{item.label}</td>
            <td className="text-muted-foreground py-1.5 pr-3 text-xs whitespace-nowrap">
              {item.value}
            </td>
            <td className="text-muted-foreground py-1.5 text-xs leading-snug">
              {item.description}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function ApprovalCard({
  id,
  title,
  description,
  icon,
  metadata,
  sections,
  variant,
  confirmLabel,
  cancelLabel,
  className,
  choice,
  onConfirm,
  onCancel,
  collapsibleSections,
  sectionTableHeaders,
}: ApprovalCardProps) {
  const resolvedVariant = variant ?? "default";
  const resolvedConfirmLabel = confirmLabel ?? "Approve";
  const resolvedCancelLabel = cancelLabel ?? "Deny";
  const Icon = icon ? getLucideIcon(icon) : null;

  const [sectionsExpanded, setSectionsExpanded] = React.useState(false);
  const totalSectionItems =
    sections?.reduce((count, section) => count + section.items.length, 0) ?? 0;
  const canCollapseSections =
    !!collapsibleSections && totalSectionItems > COLLAPSED_SECTION_ITEM_LIMIT;
  const visibleSections =
    canCollapseSections && !sectionsExpanded
      ? capApprovalSections(sections ?? [], COLLAPSED_SECTION_ITEM_LIMIT).sections
      : (sections ?? []);

  const handleAction = React.useCallback(
    async (actionId: string) => {
      if (actionId === "confirm") {
        await onConfirm?.();
      } else if (actionId === "cancel") {
        await onCancel?.();
      }
    },
    [onConfirm, onCancel]
  );

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCancel?.();
      }
    },
    [onCancel]
  );

  const isDestructive = resolvedVariant === "destructive";

  const actions: Action[] = [
    {
      id: "cancel",
      label: resolvedCancelLabel,
      variant: "ghost",
    },
    {
      id: "confirm",
      label: resolvedConfirmLabel,
      variant: isDestructive ? "destructive" : "default",
    },
  ];

  const viewKey = choice ? `receipt-${choice}` : "interactive";

  return (
    <div key={viewKey} className="contents">
      {choice ? (
        <ApprovalCardReceipt id={id} title={title} choice={choice} className={className} />
      ) : (
        <article
          className={cn(
            "flex w-full min-w-64 max-w-md flex-col gap-3",
            "text-foreground",
            className
          )}
          data-slot="approval-card"
          data-tool-ui-id={id}
          role="dialog"
          aria-labelledby={`${id}-title`}
          aria-describedby={description ? `${id}-description` : undefined}
          onKeyDown={handleKeyDown}
        >
          <div className="bg-card flex w-full flex-col gap-4 rounded-2xl border p-5 shadow-xs">
            <div className="flex items-start gap-3">
              {Icon && (
                <span
                  className={cn(
                    "flex size-10 shrink-0 items-center justify-center rounded-xl",
                    isDestructive
                      ? "bg-destructive/10 text-destructive"
                      : "bg-primary/10 text-primary"
                  )}
                >
                  <Icon className="size-5" />
                </span>
              )}
              <div className="flex flex-1 flex-col gap-1">
                <h2 id={`${id}-title`} className="text-base font-semibold leading-tight">
                  {title}
                </h2>
                {description && (
                  <p id={`${id}-description`} className="text-muted-foreground text-sm">
                    {description}
                  </p>
                )}
              </div>
            </div>

            {metadata && metadata.length > 0 && (
              <>
                <Separator />
                <dl className="flex flex-col gap-2 text-sm">
                  {metadata.map((item, index) => (
                    <div key={index} className="flex justify-between gap-4">
                      <dt className="text-muted-foreground shrink-0">{item.key}</dt>
                      <dd className="min-w-0 truncate">{item.value}</dd>
                    </div>
                  ))}
                </dl>
              </>
            )}

            {sections && sections.length > 0 && (
              <>
                <Separator />
                <div className="flex flex-col gap-3">
                  {visibleSections.map((section, sectionIndex) => (
                    <section key={sectionIndex} className="flex flex-col gap-1.5">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="text-sm font-medium leading-tight">{section.heading}</h3>
                        {section.badge && (
                          <span className="bg-primary/10 text-primary shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                            {section.badge}
                          </span>
                        )}
                      </div>
                      {section.items.length > 0 &&
                        (sectionTableHeaders ? (
                          <ApprovalSectionTable
                            items={section.items}
                            headers={sectionTableHeaders}
                          />
                        ) : (
                          <dl className="flex flex-col gap-1 pl-3 text-sm">
                            {section.items.map((item, itemIndex) => (
                              <div key={itemIndex} className="flex flex-col gap-0.5">
                                <div className="flex items-center justify-between gap-3">
                                  <dt className="min-w-0 truncate text-xs">{item.label}</dt>
                                  {item.value && (
                                    <dd className="bg-muted text-muted-foreground shrink-0 rounded-md px-1.5 py-0.5 text-[11px]">
                                      {item.value}
                                    </dd>
                                  )}
                                </div>
                                {item.description && (
                                  <dd className="text-muted-foreground text-xs leading-snug">
                                    {item.description}
                                  </dd>
                                )}
                              </div>
                            ))}
                          </dl>
                        ))}
                    </section>
                  ))}
                  {canCollapseSections && (
                    <button
                      type="button"
                      onClick={() => setSectionsExpanded((expanded) => !expanded)}
                      aria-expanded={sectionsExpanded}
                      className="text-muted-foreground hover:text-foreground self-start text-xs font-medium"
                    >
                      {sectionsExpanded ? "Show fewer" : `Show all ${totalSectionItems} fields`}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="@container/actions">
            <ActionButtons actions={actions} onAction={handleAction} />
          </div>
        </article>
      )}
    </div>
  );
}
